import { Close } from '@mui/icons-material';
import { Button, Card, Container, IconButton, Modal, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';
import { APP_SIDEBAR_WIDTH } from '/@/components/app-navbar/sidebar';
import Drawing from '/@/components/drawing';
import FileDropper from '/@/components/file-dropper';
import Spinner from '/@/components/spinner';
import settings, { isExpertoBayer } from '/@/core/settings';
import { calculateArea } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import type { TNewField } from '..';
import type { NewFieldOption } from '../components/options-selector';

const UploadField = ({
    newField,
    setNewField,
    setSelectedOption,
}: {
    newField: TNewField | undefined;
    setNewField: React.Dispatch<React.SetStateAction<TNewField | undefined>>;
    setSelectedOption: React.Dispatch<React.SetStateAction<NewFieldOption | undefined>>;
}) => {
    const navigate = useNavigate();
    const [files, setFiles] = useState<File[]>([]);
    const [isValid, setIsValid] = useState<boolean>(false);

    const useUploadFiles = useSWRMutation('useUploadFieldFiles', async (key, { arg: files }: { arg: any[] }) => {
        const formdata = new FormData();
        files.forEach((f) => formdata.append('files[]', f));

        const res = await axFetch.post(`${settings.urls.apiDomain}/core/v1/fields/parser/`, formdata);

        const coordinates = res.data.coordinates;
        if (coordinates.length === 0) throw Error('No coordinates');
        setNewField((f) => ({ ...f, geometry: coordinates, area: calculateArea(coordinates), source: 'file-upload' }));
    });

    const requiredExtensions = useMemo(
        () => [['.shx', '.shp', '.dbf'], ['.zip'], ['.geojson'], ['.kml'], ['.kmz']],
        []
    );

    return (
        <Container
            maxWidth='md'
            sx={{
                position: 'absolute',
                top: '50%',
                left: { xs: '50%', sm: `calc(50% + ${APP_SIDEBAR_WIDTH} / 2)` },
                transform: 'translate(-50%, -50%)',
            }}
        >
            <Stack sx={{ background: 'white', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '4px' }}>
                <Stack
                    sx={{
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    }}
                    alignItems={'center'}
                    p={2}
                >
                    <Typography fontWeight={500} fontSize={'20px'}>
                        {_('Subir lote desde un archivo')}
                    </Typography>
                </Stack>

                {useUploadFiles.isMutating ? (
                    <Stack alignItems={'center'} py={8}>
                        <Spinner />
                    </Stack>
                ) : (
                    <FileDropper
                        requiredExtensions={requiredExtensions}
                        files={files}
                        setFiles={setFiles}
                        onValidFiles={(validFiles) => {
                            if (validFiles.length) {
                                setIsValid(true);
                            } else setIsValid(false);
                        }}
                    />
                )}

                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    sx={{
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                    }}
                    alignItems={'center'}
                    p={2}
                >
                    <Button
                        color='secondary'
                        variant='text'
                        onClick={() => {
                            if (isExpertoBayer()) {
                                navigate(-1);
                            } else {
                                setFiles([]);
                                setSelectedOption(undefined);
                            }
                        }}
                    >
                        {_('Cancelar')}
                    </Button>
                    <Button
                        color='secondary'
                        variant='contained'
                        onClick={() => useUploadFiles.trigger(files)}
                        disabled={!isValid}
                        data-testid='upload-field-button-upload'
                    >
                        {_('Cargar')}
                    </Button>
                </Stack>

                {useUploadFiles.error && (
                    <Modal
                        open={useUploadFiles.error}
                        onClose={() => {
                            useUploadFiles.reset();
                            setFiles([]);
                        }}
                    >
                        <Container
                            maxWidth='sm'
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            <Card
                                sx={{
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                    borderRadius: '4px',
                                    px: 2,
                                    py: 4,
                                    position: 'relative',
                                }}
                            >
                                <IconButton
                                    sx={{ position: 'absolute', right: '4px', top: '4px' }}
                                    onClick={() => {
                                        useUploadFiles.reset();
                                        setFiles([]);
                                    }}
                                >
                                    <Close />
                                </IconButton>
                                <Stack alignItems='center' justifyContent='center' spacing={2} textAlign={'center'}>
                                    <Drawing type='error' />
                                    <Typography fontWeight={500} fontSize={'20px'}>
                                        {_('Ha ocurrido un error...')}
                                    </Typography>
                                    <Typography>
                                        {_('El archivo es inválido o no soportado, intenta subir otro archivo.')}
                                    </Typography>
                                </Stack>
                            </Card>
                        </Container>
                    </Modal>
                )}
            </Stack>
        </Container>
    );
};

export default UploadField;
